import { Alert, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { getInstructions, type PAInstructions } from '../api/payments';
import { Markdown } from '../money/Markdown';
import { FactList } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';

/**
 * ============================================================================
 * «ДРУГИЕ СПОСОБЫ ОПЛАТЫ» — §8e, and it is a READING page
 * ============================================================================
 *
 * The operator's own instructions for paying through a channel the portal cannot execute:
 * at the office counter, or at an OSMP terminal. No form, no input, no button that starts
 * a payment — §8e says exactly that, and it is the point: this is where a subscriber goes
 * when the card route has just failed him, so a second thing to press that might also fail
 * is not help.
 *
 * # Why it exists at all: the W2 review's finding E8
 *
 * The suspension notice named a figure and offered NO WAY TO PAY IT. «Код оплаты» renders
 * only when `contract.payment_code` is non-empty, and that column is `NOT NULL DEFAULT ''`
 * with no generator — blank on a real imported estate, present in every fixture in this
 * repository. A suspended subscriber therefore read «внесите 1 200,00» beside nothing at
 * all.
 *
 * So this page is rendered WHATEVER the payment code holds: with a code it shows the code,
 * without one it says to quote the contract number instead, and with no blocks at all it
 * says so rather than showing an empty screen. A page that 404'd or blanked when the
 * operator had written nothing would have re-created the same silence one layer down.
 *
 * # ⚠ THE BODY IS STAFF-AUTHORED MARKDOWN IN A SUBSCRIBER'S BROWSER
 *
 * It is rendered by `src/money/Markdown.tsx`, which builds React ELEMENTS and never an
 * HTML string, and which has no image, embed or style construct at all. Two consequences,
 * both of them the reason the renderer is written that way rather than as a sanitizer over
 * `dangerouslySetInnerHTML`:
 *
 *   - **stored XSS is not filtered, it is inexpressible.** Raw markup in a body becomes
 *     visible text, because there is no parser that would turn it into nodes. The server
 *     refuses raw HTML at SAVE as well; both doors, deliberately;
 *   - **no external asset can enter through operator content.** The reader this page
 *     exists for is inside the walled garden, whose allow-list covers this cabinet and the
 *     learned acquirer domains and nothing else. `src/bundle.test.ts` holds that rule for
 *     the BUNDLE and cannot see a body typed into the admin UI tomorrow — a renderer with
 *     an `<img>` in it would defeat that guard at runtime, with content the guard never
 *     scans.
 */
export function InstructionsScreen() {
  return (
    <MoneyPage titleKey="instructions.title" leadKey="instructions.lead">
      {(contract) => <InstructionsBody contractID={contract.id} contractNumber={contract.number} />}
    </MoneyPage>
  );
}

function InstructionsBody({
  contractID,
  contractNumber,
}: {
  contractID: number;
  contractNumber: string;
}) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PAInstructions>(
    () => getInstructions(contractID),
    [contractID],
  );

  return (
    <QueryFrame query={query} reload={reload}>
      {(page) => (
        <>
          {/* THE ONE DYNAMIC VALUE THE PAGE CARRIES, and the one an OSMP terminal asks
              for. It is first because a subscriber standing at a terminal is looking for
              exactly this. */}
          <Card className="pa-block">
            {page.payment_code ? (
              <FactList
                facts={[
                  {
                    key: 'code',
                    label: t('instructions.paymentCode'),
                    value: <span className="mono">{page.payment_code}</span>,
                    hint: t('instructions.paymentCodeHint'),
                  },
                ]}
              />
            ) : (
              <p className="pa-field-hint">
                {t('instructions.noCode', { number: contractNumber })}
              </p>
            )}
          </Card>

          {page.items.length === 0 ? (
            <Alert type="info" showIcon message={t('instructions.empty')} />
          ) : (
            page.items.map((block) => (
              <Card className="pa-block" key={block.code} title={block.title}>
                <Markdown text={block.body} />
              </Card>
            ))
          )}
        </>
      )}
    </QueryFrame>
  );
}
